/**
 * Prompts API client with TanStack Query hooks
 * Provides functions and hooks for prompt management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  PromptListResponse,
  PromptDetail,
  PromptSummary,
  PromptAuditRecord,
  PromptDiffResponse,
  CreatePromptRequest,
  CreateVersionRequest,
  ActivateVersionRequest,
  SetExperimentalRequest,
  RollbackRequest,
  PromptVersion,
  PromptStatus,
} from '@/types/prompts'
import { apiFetch } from './http'

// ============================================================================
// URL and Auth Helpers
// ============================================================================

/**
 * Get the orchestrator API base URL from config
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for prompts
 */
export const promptsKeys = {
  all: ['prompts'] as const,
  list: () => ['prompts', 'list'] as const,
  detail: (promptName: string) => ['prompts', 'detail', promptName] as const,
  audit: (promptName: string) => ['prompts', 'audit', promptName] as const,
  diff: (promptName: string, fromVersion: number, toVersion: number) =>
    ['prompts', 'diff', promptName, fromVersion, toVersion] as const,
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all prompts
 */
export async function fetchPrompts(): Promise<PromptListResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts`

  const response = await apiFetch(url)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Backend response format for GetPrompt endpoint
 */
interface GetPromptApiResponse {
  promptName: string
  current: {
    version: number
    content: string
    status: string
    experimentalPercentage: number | null
    metadata: {
      agent: string | null
      category: string | null
      tokenEstimate: number | null
      contentHash: string | null
    } | null
    createdAt: string
    createdBy: string
    activatedAt: string | null
    changeNote: string | null
  } | null
  experimental: {
    version: number
    content: string
    status: string
    experimentalPercentage: number | null
    metadata: {
      agent: string | null
      category: string | null
      tokenEstimate: number | null
      contentHash: string | null
    } | null
    createdAt: string
    createdBy: string
    activatedAt: string | null
    changeNote: string | null
  } | null
  history: Array<{
    version: number
    status: string
    createdAt: string
    createdBy: string
    changeNote: string | null
  }>
}

/**
 * Transform backend response to frontend PromptDetail type
 */
function mapApiResponseToPromptDetail(response: GetPromptApiResponse): PromptDetail {
  const current = response.current
  const experimental = response.experimental
  
  // Map history to versions (we don't have full content for history items)
  const versions: PromptVersion[] = response.history.map(h => ({
    version: h.version,
    status: h.status as PromptStatus,
    content: '', // History items don't include content
    changeNote: h.changeNote,
    createdAt: h.createdAt,
    createdBy: h.createdBy,
    activatedAt: null,
    activatedBy: null,
  }))
  
  // If we have current, update that version's content
  if (current) {
    const currentIdx = versions.findIndex(v => v.version === current.version)
    if (currentIdx >= 0) {
      versions[currentIdx] = {
        ...versions[currentIdx],
        content: current.content,
        activatedAt: current.activatedAt,
      }
    }
  }

  return {
    promptName: response.promptName,
    currentVersion: current?.version ?? 0,
    status: (current?.status ?? 'draft') as PromptStatus,
    agent: current?.metadata?.agent ?? 'unknown',
    category: current?.metadata?.category ?? 'unknown',
    content: current?.content ?? '',
    tokenEstimate: current?.metadata?.tokenEstimate ?? null,
    contentHash: current?.metadata?.contentHash ?? null,
    experimentalPercentage: experimental?.experimentalPercentage ?? null,
    experimentalVersion: experimental?.version ?? null,
    createdAt: current?.createdAt ?? new Date().toISOString(),
    createdBy: current?.createdBy ?? 'unknown',
    updatedAt: current?.createdAt ?? new Date().toISOString(),
    updatedBy: current?.createdBy ?? 'unknown',
    versions,
    auditTrail: [], // Audit trail is fetched separately
  }
}

/**
 * Fetch prompt detail by name
 */
export async function fetchPromptDetail(promptName: string): Promise<PromptDetail> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}`

  const response = await apiFetch(url)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  const apiResponse: GetPromptApiResponse = await response.json()
  return mapApiResponseToPromptDetail(apiResponse)
}

/**
 * Fetch audit trail for a prompt
 */
export async function fetchPromptAudit(promptName: string): Promise<PromptAuditRecord[]> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/audit`

  const response = await apiFetch(url)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Fetch diff between two versions
 */
export async function fetchPromptDiff(
  promptName: string,
  fromVersion: number,
  toVersion: number
): Promise<PromptDiffResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/diff?from=${fromVersion}&to=${toVersion}`

  const response = await apiFetch(url)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Create a new prompt
 */
export async function createPrompt(data: CreatePromptRequest): Promise<PromptSummary> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts`

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Create a new version of an existing prompt
 */
export async function createVersion(
  promptName: string,
  data: CreateVersionRequest
): Promise<PromptSummary> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/versions`

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Activate a specific version
 */
export async function activateVersion(
  promptName: string,
  version: number,
  data?: ActivateVersionRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/activate/${version}`

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data || {}),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

/**
 * Rollback to previous version
 */
export async function rollbackPrompt(
  promptName: string,
  data?: RollbackRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/rollback`

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data || {}),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

/**
 * Set experimental routing percentage
 */
export async function setExperimental(
  promptName: string,
  data: SetExperimentalRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = `${baseUrl}api/configuration/prompts/${encodeURIComponent(promptName)}/experiment`

  const response = await apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook for fetching all prompts
 */
export function usePrompts() {
  return useQuery({
    queryKey: promptsKeys.list(),
    queryFn: fetchPrompts,
  })
}

/**
 * Hook for fetching prompt detail
 */
export function usePromptDetail(promptName: string | null) {
  return useQuery({
    queryKey: promptsKeys.detail(promptName || ''),
    queryFn: () => fetchPromptDetail(promptName!),
    enabled: !!promptName,
  })
}

/**
 * Hook for fetching prompt audit trail
 */
export function usePromptAudit(promptName: string | null) {
  return useQuery({
    queryKey: promptsKeys.audit(promptName || ''),
    queryFn: () => fetchPromptAudit(promptName!),
    enabled: !!promptName,
  })
}

/**
 * Hook for fetching prompt diff
 */
export function usePromptDiff(
  promptName: string | null,
  fromVersion: number | null,
  toVersion: number | null
) {
  return useQuery({
    queryKey: promptsKeys.diff(promptName || '', fromVersion || 0, toVersion || 0),
    queryFn: () => fetchPromptDiff(promptName!, fromVersion!, toVersion!),
    enabled: !!promptName && fromVersion !== null && toVersion !== null,
  })
}

/**
 * Mutation hook for creating a new prompt
 */
export function useCreatePrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptsKeys.all })
    },
  })
}

/**
 * Mutation hook for creating a new version
 */
export function useCreateVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ promptName, data }: { promptName: string; data: CreateVersionRequest }) =>
      createVersion(promptName, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: promptsKeys.detail(variables.promptName) })
      queryClient.invalidateQueries({ queryKey: promptsKeys.list() })
    },
  })
}

/**
 * Mutation hook for activating a version
 */
export function useActivateVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      promptName,
      version,
      data,
    }: {
      promptName: string
      version: number
      data?: ActivateVersionRequest
    }) => activateVersion(promptName, version, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: promptsKeys.detail(variables.promptName) })
      queryClient.invalidateQueries({ queryKey: promptsKeys.list() })
    },
  })
}

/**
 * Mutation hook for rollback
 */
export function useRollbackPrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ promptName, data }: { promptName: string; data?: RollbackRequest }) =>
      rollbackPrompt(promptName, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: promptsKeys.detail(variables.promptName) })
      queryClient.invalidateQueries({ queryKey: promptsKeys.list() })
    },
  })
}

/**
 * Mutation hook for setting experimental
 */
export function useSetExperimental() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ promptName, data }: { promptName: string; data: SetExperimentalRequest }) =>
      setExperimental(promptName, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: promptsKeys.detail(variables.promptName) })
      queryClient.invalidateQueries({ queryKey: promptsKeys.list() })
    },
  })
}
